import React from "react";
import SectionWithButtonsLayout from "../../shared/layout/SectionWithButtonsLayout";
import DividerAccordionLayout from "../../shared/accordions/DividerAccordionLayout";
import CardSkeleton from "../../shared/loaders/CardSkeleton";
import GeneratedChartCard from "./GeneratedChartCard";
import { useDatasources } from "../../context/DatasourceContext";
import { useLoading } from "../../context/LoadingContext";
import { useProject } from "../../context/ProjectContext";
import { NO_PROJECT_EMPTY_STATE } from "../../constants";
import type { ChartCardProps } from "../../../excel/charts/api";
import { UiStateCacheKey } from "../../../excel/workbook/cacheKeys";

interface ChartsSectionProps {
  handleAddClick: () => void;
  charts: ChartCardProps[];
  loadingCharts: boolean;
  reloadCharts: () => Promise<void>;
}

const SKELETON_COUNT = 3;

const groupCacheKey = (worksheetName: string): UiStateCacheKey => `chartGroupExpanded_${worksheetName}`;

/**
 * The whole insights page as one section, with charts grouped by the worksheet they live on.
 */
const ChartsSection = ({ handleAddClick, charts, loadingCharts, reloadCharts }: ChartsSectionProps) => {
  const { hasDatasource } = useDatasources();
  const { globalLoading } = useLoading();
  const { selectedProject } = useProject();
  // Charts are estimate-only: every type resolves an estimate id and fetches the estimate export. Asked
  // of the datasource rather than the estimate directly, so it stays a deliberate restriction as other
  // datasources gain cards of their own.
  const canAdd = hasDatasource("ediphi", "estimate");

  // Unlike reports, the middle message names the estimate: a project with only a GCGR datasource still
  // cannot have charts, and nothing else in the UI says so.
  const emptyStateText = !selectedProject
    ? NO_PROJECT_EMPTY_STATE
    : canAdd
      ? "Add a chart to visualize your Ediphi data in this workbook."
      : "Load estimate data to get started.";

  // A chart lands on whatever sheet was active when it was created, so this is where it lives rather
  // than what it is. Sheets can be renamed, which orphans a group's collapsed state — accepted.
  const groups = charts.reduce<Record<string, ChartCardProps[]>>((acc, chart) => {
    (acc[chart.worksheetName] = acc[chart.worksheetName] || []).push(chart);
    return acc;
  }, {});

  const renderCards = () => {
    if (loadingCharts) {
      return Array.from({ length: SKELETON_COUNT }, (_, index) => <CardSkeleton key={index} />);
    }

    const sheets = Object.keys(groups);
    if (!sheets.length) return null;

    return sheets.map((worksheetName) => (
      <DividerAccordionLayout key={worksheetName} title={worksheetName} cacheKey={groupCacheKey(worksheetName)}>
        {groups[worksheetName].map((chart) => (
          <GeneratedChartCard key={chart.chartName} chart={chart} onReload={reloadCharts} />
        ))}
      </DividerAccordionLayout>
    ));
  };

  return (
    <SectionWithButtonsLayout
      header={{ text: "Insights" }}
      primaryButton={{ onClick: handleAddClick, disabled: !canAdd || globalLoading }}
      emptyStateText={emptyStateText}
    >
      {renderCards()}
    </SectionWithButtonsLayout>
  );
};

export default ChartsSection;
